/**
 * AgentPlaygroundDialog — sandbox testowania promptów agenta AI.
 *
 * WAŻNE:
 *  - NIE zapisuje wyniku jako danych produkcyjnych.
 *  - NIE modyfikuje pamięci kontrahentów ani innych kolekcji.
 *  - Domyślnie używa promptów z ustawień agenta, ale można je nadpisać lokalnie.
 *  - Klucz API NIE jest nigdy widoczny ani edytowalny.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Play, X, Bot, AlertTriangle, CheckCircle2, Copy, ClipboardCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { logger } from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Domyślne dane testowe per agent (tylko Agent Faktur ma sensowny payload).
const DEFAULT_TEST_PAYLOAD = {
  invoice_agent: {
    vendor_name: "Seweryn Nowosielski",
    vendor_nip: "1251360819",
    payment_type: "przelew",
    invoices: [
      {
        invoice_number: "FV/1/2026",
        issue_date: "2026-06-01",
        gross_amount: 1230.00,
        currency: "PLN",
      },
    ],
  },
  bank_agent: {
    _placeholder: "Agent Wyciągów nie jest jeszcze wdrożony — to przykładowy JSON",
    transactions: [
      {
        date: "2026-06-02",
        amount: -1230.00,
        currency: "PLN",
        counterparty_name: "Seweryn Nowosielski",
        description: "FV/1/2026",
      },
    ],
  },
  payment_agent: {
    _placeholder: "Agent Analizy nie jest jeszcze wdrożony — to przykładowy JSON",
    pending_invoices: [],
    matched_transactions: [],
  },
};

const AgentPlaygroundDialog = ({ open, onOpenChange, agent }) => {
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [testPayloadStr, setTestPayloadStr] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !agent) return;
    setSystemPrompt(agent.system_prompt || "");
    setUserPrompt(agent.user_prompt_template || "");
    const payload = DEFAULT_TEST_PAYLOAD[agent.agent_id] || {};
    setTestPayloadStr(JSON.stringify(payload, null, 2));
    setResult(null);
  }, [open, agent]);

  const handleRun = async () => {
    if (!agent) return;
    let payload = null;
    if (testPayloadStr.trim()) {
      try {
        payload = JSON.parse(testPayloadStr);
      } catch (e) {
        toast.error("Test payload nie jest poprawnym JSON");
        return;
      }
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/agents/settings/${agent.agent_id}/playground`,
        {
          system_prompt: systemPrompt,
          user_prompt: userPrompt,
          test_payload: payload,
        },
        { withCredentials: true },
      );
      setResult(res.data);
    } catch (e) {
      logger.error("playground error", e);
      const detail = e.response?.data?.detail || "Błąd uruchomienia testu";
      const msg = typeof detail === "string" ? detail : "Błąd uruchomienia testu";
      toast.error(msg);
      setResult({ ok: false, error: msg, provider: agent.provider, model: agent.model });
    } finally {
      setRunning(false);
    }
  };

  // === Kopiowanie do schowka — mobile-safe (fallback execCommand dla iOS Safari) ===
  const copyToClipboard = async (text, successLabel) => {
    if (!text) {
      toast.error("Brak treści do skopiowania");
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback: tymczasowa textarea + execCommand('copy')
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.setAttribute("readonly", "");
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(successLabel || "Skopiowano");
    } catch (err) {
      logger.error("clipboard copy failed", err);
      toast.error("Nie udało się skopiować do schowka");
    }
  };

  // Buduje plaintext z całą zawartością prompta (system + user + payload).
  const buildPromptText = () =>
    `System prompt:\n${systemPrompt || "(puste)"}\n\n` +
    `User prompt:\n${userPrompt || "(puste)"}\n\n` +
    `Payload:\n${testPayloadStr || "(puste)"}`;

  // Buduje plaintext "Kopiuj cały test" — provider, model, prompty, payload, response.
  const buildFullTestText = () => {
    const provider = result?.provider || agent?.provider || "—";
    const model = result?.model || agent?.model || "—";
    const response = result?.raw_response || result?.error || "(brak odpowiedzi)";
    return (
      `Provider: ${provider}\n` +
      `Model: ${model}\n\n` +
      `System prompt:\n${systemPrompt || "(puste)"}\n\n` +
      `User prompt:\n${userPrompt || "(puste)"}\n\n` +
      `Payload:\n${testPayloadStr || "(puste)"}\n\n` +
      `Response:\n${response}`
    );
  };

  if (!agent) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="agent-playground-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            Playground — {agent.agent_name}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono">
              {agent.agent_id}
            </span>
            <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">
              {agent.provider}
            </span>
            <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 font-mono">
              {agent.model}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div
          className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded px-3 py-2 flex items-start gap-2"
          data-testid="playground-sandbox-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Tryb sandbox.</strong> Wynik testu NIE jest zapisywany jako dane produkcyjne.
            Nie modyfikuje pamięci kontrahentów, nie tworzy paczek, nie wpływa na faktury w KSeF Inbox.
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Lewa kolumna: input */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs text-gray-600 uppercase tracking-wider">Prompt</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 sm:h-8 px-3 text-xs"
                onClick={() => copyToClipboard(buildPromptText(), "Skopiowano prompt")}
                data-testid="playground-copy-prompt-btn"
                aria-label="Kopiuj cały prompt (system + user + payload)"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Kopiuj prompt
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">System prompt</Label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={6}
                className="font-mono text-xs"
                data-testid="playground-system-prompt"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">User prompt template</Label>
              <Textarea
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={5}
                className="font-mono text-xs"
                data-testid="playground-user-prompt"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">
                Dane testowe (JSON) — dołączane do user prompt
              </Label>
              <Textarea
                value={testPayloadStr}
                onChange={(e) => setTestPayloadStr(e.target.value)}
                rows={8}
                className="font-mono text-xs"
                data-testid="playground-test-payload"
              />
            </div>
          </div>

          {/* Prawa kolumna: output */}
          <div className="space-y-3" data-testid="playground-output">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Wynik</Label>
              {!result && !running && (
                <div className="border border-dashed border-gray-300 rounded p-6 text-center text-xs text-gray-500">
                  Kliknij „Uruchom test" aby zobaczyć odpowiedź modelu.
                </div>
              )}
              {running && (
                <div className="border border-dashed border-blue-300 rounded p-6 text-center text-xs text-blue-700 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Trwa wywołanie modelu…
                </div>
              )}
              {result && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {result.ok ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3.5 h-3.5" /> OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">
                        <AlertTriangle className="w-3.5 h-3.5" /> Błąd
                      </span>
                    )}
                    {typeof result.latency_ms === "number" && (
                      <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                        latency: <strong>{result.latency_ms} ms</strong>
                      </span>
                    )}
                    <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                      {result.provider} / {result.model}
                    </span>
                  </div>

                  {result.usage && (result.usage.input_tokens || result.usage.output_tokens) && (
                    <div className="text-xs text-gray-600">
                      Tokens — input: {result.usage.input_tokens ?? "—"} | output:{" "}
                      {result.usage.output_tokens ?? "—"} | total:{" "}
                      {result.usage.total_tokens ?? "—"}
                    </div>
                  )}

                  {result.error && (
                    <div
                      className="bg-red-50 border border-red-200 text-red-800 text-xs rounded px-3 py-2 whitespace-pre-wrap break-words"
                      data-testid="playground-error"
                    >
                      {result.error}
                    </div>
                  )}

                  {result.raw_response && (
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Label className="text-xs text-gray-600">Surowa odpowiedź</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-9 sm:h-8 px-3 text-xs"
                          onClick={() => copyToClipboard(result.raw_response, "Skopiowano odpowiedź")}
                          data-testid="playground-copy-response-btn"
                          aria-label="Kopiuj surową odpowiedź modelu"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1.5" /> Kopiuj odpowiedź
                        </Button>
                      </div>
                      <pre
                        className="bg-gray-900 text-gray-100 text-xs font-mono p-3 rounded overflow-x-auto max-h-64"
                        data-testid="playground-raw-response"
                      >
                        {result.raw_response}
                      </pre>
                    </div>
                  )}

                  {result.parsed_response && (
                    <div>
                      <Label className="text-xs text-gray-600">
                        Sparsowane (best-effort JSON)
                      </Label>
                      <pre
                        className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-mono p-3 rounded overflow-x-auto max-h-48"
                        data-testid="playground-parsed-response"
                      >
                        {JSON.stringify(result.parsed_response, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => copyToClipboard(buildFullTestText(), "Skopiowano cały test")}
            disabled={running}
            data-testid="playground-copy-full-btn"
            className="h-10 sm:h-9 w-full sm:w-auto"
            aria-label="Kopiuj cały test (provider, model, prompty, payload, response)"
          >
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> Kopiuj cały test
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
            data-testid="playground-close-btn"
            className="h-10 sm:h-9 w-full sm:w-auto"
          >
            <X className="w-4 h-4 mr-1" /> Zamknij
          </Button>
          <Button
            onClick={handleRun}
            disabled={running}
            data-testid="playground-run-btn"
            className="bg-blue-600 hover:bg-blue-700 h-10 sm:h-9 w-full sm:w-auto"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            Uruchom test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentPlaygroundDialog;
